// PermissionGate — replaces the legacy RoleGate (#92).
// Reads useAuthStore().permissions; renders <Outlet /> when every required
// permission is satisfied (or `permissions === 'ALL'`), else renders
// <ErrorState>. Optional `denySystemRoles` (#1183) layers a system-role
// deny check on top: when the current user is a SYSTEM role (isSystem===true)
// whose `name` is in the list, the gate refuses regardless of permissions.
import { Outlet, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { PermissionCode } from '@magimp/shared';
import { hasPermission, useAuthStore } from '../stores/auth.js';
import { ErrorState } from '../ui/index.js';

export interface PermissionGateProps {
  permissions: PermissionCode[];
  // WHY (#1183): frontend-only deny for SEEDED system roles. Backend RBAC
  //   stays authoritative for API access; this prop hides standalone
  //   pages from a system role that nominally holds the underlying
  //   permission (e.g. Manager keeps `sources.read` for lookup data
  //   inside allowed forms but must not open `/sources`). Custom
  //   (`isSystem === false`) roles are NEVER matched, even on a name
  //   collision.
  denySystemRoles?: readonly string[];
}

export function PermissionGate({ permissions, denySystemRoles }: PermissionGateProps) {
  const { t } = useTranslation('common');
  const granted = useAuthStore((s) => s.permissions);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const deniedByRole =
    denySystemRoles !== undefined &&
    user?.role?.isSystem === true &&
    denySystemRoles.includes(user.role.name);
  const allowed = !deniedByRole && permissions.every((code) => hasPermission(granted, code));
  if (!allowed) {
    return (
      <ErrorState
        title={t('states.forbiddenTitle')}
        text={t('states.forbiddenText')}
        onRetry={() => navigate('/')}
        retryLabel={t('actions.goHome')}
      />
    );
  }
  return <Outlet />;
}
